import React, { useState } from 'react';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { TextareaInput } from '@components/atoms/TextareaInput';
import { Loading } from '@components/atoms/Loading';
import { useTranslation } from '@hooks/useTranslation';

const MAX_FILES = 10;

const AI_PLAN_ACCEPTED_TYPES: Record<string, string[]> = {
  'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
  'application/pdf': ['.pdf'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

interface BriefFormProps {
  onSubmit: (input: { brief: string; files: File[] }) => void;
  isSubmitting?: boolean;
}

/** Sprint 31, story-161. Free text + a file drop zone, up to 10 files. Submit disabled until there's a brief or a file. */
export const BriefForm: React.FC<BriefFormProps> = ({ onSubmit, isSubmitting }) => {
  const t = useTranslation('aiPlan');
  const [brief, setBrief] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const canSubmit = (brief.trim().length > 0 || files.length > 0) && !isSubmitting;

  return (
    <div className="flex flex-col gap-4" data-testid="ai-plan-brief-form">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t.briefTitle}</label>
        <TextareaInput
          rows={5}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder={t.briefPlaceholder}
          data-testid="ai-plan-brief-input"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t.attachmentsLabel}</label>
        <FileDropZone
          files={files}
          onFilesChange={(next) => setFiles(next.slice(0, MAX_FILES))}
          multiple
          maxFiles={MAX_FILES}
          acceptedTypes={AI_PLAN_ACCEPTED_TYPES}
          title={t.dropZoneHint}
          subtitle={t.dropZoneTypes}
        />
        {files.length > 0 && (
          <p className="mt-1 text-xs text-gray-400">
            {(files.length === 1 ? t.fileCountOne : t.fileCountOther).replace('{n}', String(files.length))}
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => onSubmit({ brief, files })}
          data-testid="ai-plan-generate-button"
          className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-white disabled:opacity-60 flex items-center gap-2">
          {isSubmitting && <Loading size="sm" />}
          {t.generateSubmit}
        </button>
      </div>
    </div>
  );
};

export default BriefForm;
